import { useMemo } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventWebsiteDesignQuery } from './useEventWebsiteDesign.graphql.generated';
import { useWebsiteV2DesignSite } from './useWebsiteV2DesignSite';
import { fromEvent, LOADING } from './useEventWebsiteDesign.utils';
import type { EventWebsiteDesign } from './useEventWebsiteDesign.types';

/**
 * The current event's website design in V1 shape, for surfaces outside the website editor that
 * match or tint against it (card shop, dashboard suite, …). Callers never deal with website
 * versions or sites: a Website V2 event's current site (`useWebsiteVersion`) wins, and every other
 * event reads its live charm design. See doc/website-v2/legacy-design-compat.md.
 *
 * The event is `eventHandleOverride` when given (`''` for none), else the one `useEventInfo` resolves (the route's event).
 * `ssr: false` on a route whose server render has no viewer session (no SSR auth token), where a V2
 * event's site read would run anonymous.
 */
export const useEventWebsiteDesign = (eventHandleOverride?: string, { ssr = true }: Readonly<{ ssr?: boolean }> = {}): EventWebsiteDesign => {
  const eventInfo = useEventInfo();
  const eventHandle = eventHandleOverride ?? eventInfo.eventHandle;
  const { data, loading } = useEventWebsiteDesignQuery({
    variables: { eventHandle: eventHandle ?? '' },
    batchMode: 'fast',
    ssr,
    skip: !eventHandle,
  });
  const v2 = useWebsiteV2DesignSite(eventHandle ?? '', { ssr });
  const { site } = v2;
  const isLoading = v2.isLoading || (loading && !data);
  return useMemo(() => (isLoading ? LOADING : fromEvent(data?.eventByName, site)), [isLoading, data, site]);
};
